/**
 * Material Issue (Feb 2026 · Partial Issuing)
 *
 * Store-Manager screen for RM Warehouse dispatches. Each MRQ line
 * tracks Required · Issued-to-Date · Balance; the store can post one
 * or many partial issues against the same MRQ until the balance zeros
 * out. Every posted transaction stamps SO/ESO · CWO · Department onto
 * both stock_movements and inventory_txns so the Stock Book Kardex
 * shows which customer order consumed which raw batch.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import { RowActions } from "@/components/RowActions";
import { PackageIcon, ArrowsClockwiseIcon, CheckCircleIcon, TrashIcon, StackIcon, LightningIcon, ArrowCounterClockwiseIcon, ProhibitIcon } from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const num = (v) => Number(v || 0);
// Feb 2026 · QC-Gated Inventory · issue-side guard. `passedByCode` mirrors
// the Passed pool per RM code so the "In Stock" column only shows what
// can actually be dispatched (Awaiting-Inspection lots are hidden). The
// backend already enforces the ledger — this hint keeps the UI honest.
const lineRequired = (ln) => num(ln.qty_required ?? ln.qty);
const lineIssued = (ln) => num(ln.qty_issued);
const lineBalance = (ln) => Math.max(0, +(lineRequired(ln) - lineIssued(ln)).toFixed(4));

const statusBadge = (s, reversed_at) => {
  // Feb 27, 2026 · When an MRQ was reversed we now leave status="Open"
  // so it reappears on the pending list, but paint the badge amber
  // with a dashed border + "Reversed" label so operators know its
  // history at a glance.
  if (reversed_at) return "bg-amber-500/10 text-amber-300 border-dashed border-amber-500/60";
  if (s === "Issued") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  if (s === "Partially Issued") return "bg-sky-500/15 text-sky-300 border-sky-500/30";
  return "bg-amber-500/15 text-amber-300 border-amber-500/30";
};

export default function MaterialIssue() {
  const [mrqs, setMrqs] = useState([]);
  const [balances, setBalances] = useState([]);
  // Feb 27, 2026 · Reversal Impact Preview · when the operator clicks the
  // reverse button we fetch a read-only preview from the backend and show
  // this dialog with the full cascade blast-radius before asking for a
  // reason. `revTarget` holds the MRQ row + preview payload.
  const [revTarget, setRevTarget] = useState(null);   // {row, preview}
  const [revReason, setRevReason] = useState("");
  const [revBusy, setRevBusy] = useState(false);
  // Feb 2026 · QC-passed on-hand pool per RM id (raw_material_id → qty).
  // Populated once per load from /stock/on-hand?quality_status=Passed so
  // the Material Issue "In Stock" column excludes Awaiting-Inspection
  // and Rejected lots. Prevents staff from staging an issue against
  // stock the QC Manager hasn't cleared yet.
  const [passedStock, setPassedStock] = useState({});
  const [rmIdByCode, setRmIdByCode] = useState({});
  // Feb 25 · Source-Godown filter · godown_code → godown_name map so
  // stockFor can look up the correct balances row for any WIP source,
  // not just the RM Store.
  const [godownNameByCode, setGodownNameByCode] = useState({});
  const [loading, setLoading] = useState(false);
  const [so, setSo] = useState("all");
  const [dept, setDept] = useState("all");
  const [cwo, setCwo] = useState("all");
  // Feb 25 · Source Godown filter · defaults to "all" so no requisitions
  // are hidden. Populated dynamically from the MRQ list below.
  const [sourceGodown, setSourceGodown] = useState("all");
  const [picked, setPicked] = useState(null);
  // Per-line qty the user wants to issue *this* transaction. Keyed by
  // rm_code and always ≤ current balance for that line.
  const [issueQty, setIssueQty] = useState({});
  // Feb 25 · Batch-Specific Issuing · per rm_code, hold the loaded
  // list of active lots and the per-lot qty the operator wants to
  // draw. `batchOpen` toggles the drawer's visibility inline.
  const [batchesByRm, setBatchesByRm] = useState({});   // rm_code → [{lot_id,batch_no,received_date,available_qty}] (sorted oldest → newest for FIFO)
  const [batchQty, setBatchQty] = useState({});          // `${rm_code}|${lot_id}` → qty
  const [batchOpen, setBatchOpen] = useState({});        // rm_code → bool
  // Feb 2026 · FIFO Suggestion · keys (`${rm_code}|${lot_id}`) whose
  // qty was auto-filled by the FIFO helper. Cleared on manual edit so
  // the "FIFO" badge disappears the moment the operator overrides.
  const [saving, setSaving] = useState(false);
  const [fifoFilled, setFifoFilled] = useState({});
  // P17-UAT MI consolidation · Destination Godown control on the Processing
  // Request. Source stays RM-STORE; destination is resolved LIVE from Godown
  // Master filtered by the MRQ department (auto-select single · require pick
  // when many · fail-closed when none). Mandatory before Post Issue.
  const [godownInfo, setGodownInfo] = useState(null);
  const [destGodownId, setDestGodownId] = useState("");

  // Feb 25 · Mandatory Batch Selection · the "To Issue Now" input is
  // read-only and derived exclusively from batch allocations. This
  // helper returns the sum of batch qtys entered for a given RM line.
  const batchSum = (rm_code) => {
    const lots = batchesByRm[rm_code] || [];
    return lots.reduce((s, l) => s + num(batchQty[`${rm_code}|${l.lot_id}`]), 0);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: m }, { data: b }, { data: onhand }, { data: rms }, { data: gs }] = await Promise.all([
        api.get("/material-requisitions"),
        api.get("/godown-balances"),
        // Feb 2026 · Passed-only pool used to gate the "In Stock" column.
        api.get("/stock/on-hand?quality_status=Passed"),
        api.get("/raw-materials/"),
        api.get("/godowns/").catch(() => ({ data: [] })),
      ]);
      // Feb 27, 2026 · Warehouse Scope · Material Issue is strictly for
      // MRQs tagged `Warehouse Fulfillment` (Warping + Weaving).
      // Internal Handoff MRQs (Covering / Spandex Warping) flow through
      // the internal departmental transfer workflows instead.
      const { fulfillmentType, FULFILLMENT_WAREHOUSE } = await import("@/lib/fulfillment");
      setMrqs((m || []).filter(row => fulfillmentType(row.department) === FULFILLMENT_WAREHOUSE));
      setBalances(b || []);
      // Aggregate lot-level rows into RM-level pool (Passed only).
      const pool = {};
      for (const r of (onhand?.rows || onhand || [])) {
        const rid = r.raw_material_id;
        if (rid) pool[rid] = (pool[rid] || 0) + num(r.available_qty);
      }
      setPassedStock(pool);
      const codeMap = {};
      for (const r of (rms || [])) codeMap[r.code] = r.id;
      setRmIdByCode(codeMap);
      const gMap = {};
      for (const g of (gs || [])) if (g.code) gMap[g.code] = g.name || g.code;
      setGodownNameByCode(gMap);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // P17-UAT MI consolidation · load source (RM-STORE) + eligible destination
  // godowns for the picked MRQ. Auto-select when exactly one; require an
  // explicit pick when many; fail-closed (block Post Issue) when none.
  useEffect(() => {
    if (!picked) { setGodownInfo(null); setDestGodownId(""); return; }
    (async () => {
      try {
        const { data } = await api.get(`/material-issues/${picked.id}/godowns`);
        setGodownInfo(data);
        setDestGodownId(data.auto_select_id || "");
      } catch (e) {
        setGodownInfo(null); setDestGodownId("");
        toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to load destination godowns");
      }
    })();
  }, [picked?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const stockFor = (rm_code, src) => {
    // Feb 2026 · When RM Store is the source, the visible pool is
    // strictly QC-Passed stock (Awaiting/Rejected lots never appear).
    // For non-RM sources (WIP floors like COV-4FLR), look up the exact
    // balances row by godown_name (resolved from code via the code→name
    // map fetched on load).
    if (src === "RM-STORE" || src === "RM-WH") {
      const rid = rmIdByCode[rm_code];
      return rid ? num(passedStock[rid]) : 0;
    }
    const gname = godownNameByCode[src] || "";
    const row = balances.find(b => b.rm_code === rm_code && (gname
      ? b.godown_name === gname
      : (b.godown_name || "").includes((src || "").split("-")[0])));
    return num(row?.balance);
  };

  const sos = useMemo(() => Array.from(new Set(mrqs.map(m => m.source_order_no).filter(Boolean))).sort(), [mrqs]);
  const depts = useMemo(() => Array.from(new Set(mrqs.map(m => m.department))).sort(), [mrqs]);
  const cwoNos = useMemo(() => Array.from(new Set(mrqs.map(m => m.cwo_no))).sort(), [mrqs]);
  // Feb 25 · Source Godown filter · dropdown options come from the
  // union of every MRQ's source_godown_code so the picker only lists
  // sources that actually have pending requisitions.
  const sources = useMemo(
    () => Array.from(new Set(mrqs.map(m => m.source_godown_code).filter(Boolean))).sort(),
    [mrqs],
  );
  const filtered = useMemo(() => mrqs.filter(m =>
    (so === "all" || m.source_order_no === so) &&
    (dept === "all" || m.department === dept) &&
    (cwo === "all" || m.cwo_no === cwo) &&
    (sourceGodown === "all" || m.source_godown_code === sourceGodown)
  ), [mrqs, so, dept, cwo, sourceGodown]);

  const pickMrq = (r) => {
    setPicked(r);
    // Feb 25 · Mandatory Batch Selection · qty is derived from batches,
    // reset the derived cache to empty so nothing leaks from a prior pick.
    const q = {};
    (r.lines || []).forEach(ln => { q[ln.rm_code] = ""; });
    setIssueQty(q);
    // Reset per-MRQ batch state so drawers open clean for the new pick.
    setBatchesByRm({});
    setBatchQty({});
    setBatchOpen({});
    setFifoFilled({});
    setGodownInfo(null);
    setDestGodownId("");
  };

  // Feb 2026 · FIFO Suggestion helper. Sorts lots oldest → newest by
  // `received_date` (fallback to batch_no) and walks the list, draining
  // each lot's available qty until `targetQty` is met. Any prior manual
  // entries in this rm_code's batch inputs are overwritten; only rows
  // it actually touched are tagged as FIFO-suggested.
  const applyFifo = (rm_code, targetQty, lotsInput) => {
    const target = num(targetQty);
    const lots = (lotsInput || batchesByRm[rm_code] || []).slice().sort((a, b) => {
      const da = a.received_date || "";
      const db = b.received_date || "";
      if (da !== db) return da < db ? -1 : 1;
      return (a.batch_no || "").localeCompare(b.batch_no || "");
    });
    const nextQty = { ...batchQty };
    const nextTag = { ...fifoFilled };
    // Wipe previous entries for this rm_code before re-suggesting.
    for (const l of lots) {
      const k = `${rm_code}|${l.lot_id}`;
      delete nextQty[k];
      delete nextTag[k];
    }
    let remaining = target;
    if (target > 0) {
      for (const l of lots) {
        if (remaining <= 1e-6) break;
        const take = Math.min(num(l.available_qty), remaining);
        if (take > 0) {
          const k = `${rm_code}|${l.lot_id}`;
          nextQty[k] = +take.toFixed(3);
          nextTag[k] = true;
          remaining = +(remaining - take).toFixed(6);
        }
      }
    }
    setBatchQty(nextQty);
    setFifoFilled(nextTag);
    return { lots, shortfall: Math.max(0, +remaining.toFixed(3)) };
  };

  const canIssue = picked && picked.status !== "Issued";

  const issue = async () => {
    if (!picked) return;
    if (picked.status === "Issued") { toast.info(`${picked.mrq_no} · already fully issued`); return; }
    // Feb 25 · Mandatory Batch Selection · every non-zero line MUST be
    // batch-allocated. Since the "To Issue Now" field is read-only and
    // its value is `batchSum(rm)`, this guard is symmetric with the UI —
    // no batches ⇒ no qty ⇒ nothing to post.
    const linesBody = (picked.lines || [])
      .map(ln => {
        const batches = (batchesByRm[ln.rm_code] || [])
          .map(l => ({ lot_id: l.lot_id, batch_no: l.batch_no, qty: num(batchQty[`${ln.rm_code}|${l.lot_id}`]) }))
          .filter(b => b.qty > 0);
        const qty = batches.reduce((s, b) => s + b.qty, 0);
        return { rm_code: ln.rm_code, qty, batches };
      })
      .filter(l => l.qty > 0);
    if (linesBody.length === 0) {
      toast.error("Batch selection is mandatory. Please select specific batches to proceed.");
      return;
    }
    // P17-UAT MI consolidation · Destination Godown must be resolvable/selected
    // before a physical issue can be posted (fail-closed · belt + suspenders —
    // the backend also enforces this via resolve_mi_destination).
    if (godownInfo?.no_destination) {
      toast.error(`No destination godown configured in Godown Master for department "${godownInfo.department}"`);
      return;
    }
    if (godownInfo?.requires_selection && !destGodownId) {
      toast.error("Select a destination godown before posting the issue");
      return;
    }
    // Guard: refuse to over-issue any line.
    // Feb 27, 2026 · Returnable Excess Issue — Actual > Required is now
    // *allowed*. The backend flags the delta as excess_qty and auto-
    // spawns a Returnable Material Challan; warn the user once, but
    // don't block the post.
    let excessTotal = 0;
    for (const ln of picked.lines || []) {
      const req = batchSum(ln.rm_code);
      const bal = lineBalance(ln);
      if (req - bal > 0.0001) excessTotal += (req - bal);
    }
    if (excessTotal > 0.0001) {
      if (!window.confirm(`⚠️ Actual issue exceeds required by ${fmt(excessTotal)} — a Returnable Material Challan will be auto-generated for the excess. Proceed?`)) return;
    }
    const total = linesBody.reduce((s, l) => s + l.qty, 0);
    if (!window.confirm(`Post issue of ${fmt(total)} across ${linesBody.length} line(s) for ${picked.mrq_no}? Stock moves from ${picked.source_godown_code} to the ${picked.department} floor.`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/material-requisitions/${picked.id}/issue`, { lines: linesBody, destination_godown_id: destGodownId || "" });
      const done = data.status === "Issued";
      // P17-UAT MI consolidation · a single Post Issue now splits into one MI
      // per RM Item + Batch/Lot. Surface the count + first MI, with a Print
      // shortcut for the first document.
      const miNos = data.mi_numbers || (data.mi_no ? [data.mi_no] : []);
      const miCount = data.mi_count || miNos.length;
      const miId = data.mi_id || "";
      toast.success(
        miCount > 0
          ? `${picked.mrq_no} · ${done ? "fully issued" : "partial issue posted"} · ${miCount} MI${miCount === 1 ? "" : "s"} minted${miNos[0] ? ` (${miNos[0]}${miCount > 1 ? ` +${miCount - 1}` : ""})` : ""}`
          : `${picked.mrq_no} · ${done ? "fully issued" : "partial issue posted · balance carried"}`,
        miId ? {
          duration: 8000,
          action: {
            label: "Print MI Note",
            onClick: () => openPrintMi(miId),
          },
        } : undefined,
      );
      if (done) setPicked(null);
      else pickMrq(data);   // refresh form with updated balances
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Issue failed");
    } finally { setSaving(false); }
  };

  // Feb 2026 · Auto-Mirror MI · print shortcut. Reuses the shared
  // Material Issue Note template served by `/material-issues/{id}/print`.
  const openPrintMi = async (mi_id) => {
    try {
      const res = await api.get(`/material-issues/${mi_id}/print`, { responseType: "text" });
      const w = window.open("", "_blank");
      if (!w) { toast.error("Enable pop-ups to preview the Material Issue Note"); return; }
      w.document.open(); w.document.write(res.data); w.document.close();
      setTimeout(() => { try { w.focus(); w.print(); } catch (_) {} }, 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Print failed");
    }
  };

  const openCount = filtered.filter(f => f.status === "Open").length;
  const partialCount = filtered.filter(f => f.status === "Partially Issued").length;
  const doneCount = filtered.filter(f => f.status === "Issued").length;

  return (
    <div className="p-6 space-y-5" data-testid="material-issue-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><PackageIcon size={11} weight="bold" />Purchase &amp; Inventory · Material Issue Request (MIR)</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Material Issue Request (MIR)</h1>
          <div className="text-xs text-zinc-500 mt-1">Post partial or full issues against any open Material Issue Request · Warping &amp; Weaving only · Covering and Spandex Warping flow through internal transfers · every request is stamped with SO/ESO · CWO · Dept for full-loop traceability</div>
        </div>
        <button onClick={load} disabled={loading} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2" data-testid="mi-refresh">
          <ArrowsClockwiseIcon size={14} weight="bold" />Refresh
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap text-[11px]">
        <span className="text-zinc-500 font-mono uppercase tracking-widest">Source Godown:</span>
        <select value={sourceGodown} onChange={e => setSourceGodown(e.target.value)} className="h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white" data-testid="mi-filter-source-godown">
          <option value="all">All</option>
          {sources.map(s => (
            <option key={s} value={s}>{s}{godownNameByCode[s] ? ` · ${godownNameByCode[s]}` : ""}</option>
          ))}
        </select>
        <span className="text-zinc-500 font-mono uppercase tracking-widest ml-3">SO/ESO:</span>
        <select value={so} onChange={e => setSo(e.target.value)} className="h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white" data-testid="mi-filter-so">
          <option value="all">All</option>{sos.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-zinc-500 font-mono uppercase tracking-widest ml-3">Dept:</span>
        <select value={dept} onChange={e => setDept(e.target.value)} className="h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white" data-testid="mi-filter-dept">
          <option value="all">All</option>{depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <span className="text-zinc-500 font-mono uppercase tracking-widest ml-3">CWO:</span>
        <select value={cwo} onChange={e => setCwo(e.target.value)} className="h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white" data-testid="mi-filter-cwo">
          <option value="all">All</option>{cwoNos.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="ml-auto text-zinc-500 font-mono" data-testid="mi-counts">
          {openCount} open · {partialCount} partial · {doneCount} issued
        </span>
      </div>

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="mi-pending-table">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-4 py-3">MIR No</th>
              <th className="text-left px-4 py-3">Dept</th>
              <th className="text-left px-4 py-3">CWO</th>
              <th className="text-left px-4 py-3">SO/ESO</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Issued / Required</th>
              <th className="text-right px-4 py-3">Balance</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => {
              const totalReq = (r.lines || []).reduce((s, l) => s + lineRequired(l), 0);
              const totalIss = (r.lines || []).reduce((s, l) => s + lineIssued(l), 0);
              const totalBal = Math.max(0, +(totalReq - totalIss).toFixed(3));
              const done = r.status === "Issued";
              return (
                <tr key={r.id} className={`border-b border-[var(--border)] cursor-pointer ${picked?.id === r.id ? "bg-[var(--accent)]/10" : "hover:bg-[var(--bg-2)]/40"} ${done ? "opacity-70" : ""}`} onClick={() => pickMrq(r)} data-testid={`mi-row-${r.id}`}>
                  <td className="px-4 py-3 text-right"><TwoLineDocNo value={r.mrq_no} className="text-white" /></td>
                  <td className="px-4 py-3 text-zinc-300">{r.department}</td>
                  <td className="px-4 py-3 text-right"><TwoLineDocNo value={r.cwo_no} className="text-[var(--accent)]" /></td>
                  <td className="px-4 py-3 text-right"><TwoLineDocNo value={r.source_order_no || "—"} className="text-zinc-300" /></td>
                  <td className="px-4 py-3 font-mono text-amber-300">{r.source_godown_code}</td>
                  <td className="px-4 py-3">
                    <span data-testid={`mi-status-${r.id}`} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${statusBadge(r.status, r.reversed_at)}`}>
                      {r.reversed_at && lineIssued({qty_issued: totalIss}) === 0 ? "Reversed" : (r.status || "Open")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-zinc-400">
                    <span className="text-emerald-300">{fmt(totalIss)}</span> / <span className="text-white">{fmt(totalReq)}</span>
                  </td>
                  <td className={`px-4 py-3 text-right font-mono tabular-nums font-semibold ${totalBal > 0 ? "text-amber-300" : "text-emerald-400"}`}>{fmt(totalBal)}</td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <RowActions
                      testid={`mi-actions-${r.id}`}
                      items={[
                        { label: "View", testid: `mi-view-${r.id}`, onClick: () => pickMrq(r) },
                        { label: "Edit Quantity", testid: `mi-edit-${r.id}`, show: !done, onClick: () => pickMrq(r) },
                        {
                          label: "Reverse", tone: "warn", testid: `mi-reverse-${r.id}`, disabled: totalIss <= 0,
                          onClick: async () => {
                            // Feb 27, 2026 · Reversal Impact Preview · fetch
                            // the cascade blast-radius from the backend so
                            // the operator sees exactly which RMCs will be
                            // cancelled and how many ledger legs mirrored
                            // before they commit.
                            if (totalIss <= 0) {
                              toast.info(r.reversed_at ? `${r.mrq_no} is already reversed` : `${r.mrq_no} has no posted issue against this request to reverse`);
                              return;
                            }
                            try {
                              const { data } = await api.get(`/material-requisitions/${r.id}/reverse-preview`);
                              setRevTarget({ row: r, preview: data });
                              setRevReason("");
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Failed to load reversal preview");
                            }
                          },
                        },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && !loading && <tr><td colSpan={9} className="text-center py-16 text-zinc-500">No open Material Issue Requests match the filters</td></tr>}
          </tbody>
        </table>
      </div>

      {picked && (
        <div className="tactical-card p-5 space-y-4" data-testid="mi-issue-form">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Processing Request</div>
              <div className="font-mono text-white text-lg">{picked.mrq_no}</div>
              <div className="text-xs text-zinc-400 mt-1">{picked.department} · CWO {picked.cwo_no} · SO/ESO {picked.source_order_no || "—"} · from {picked.source_godown_code}</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPicked(null)} className="px-3 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400" data-testid="mi-cancel">Cancel</button>
              {canIssue
                ? (() => {
                    // Feb 25 · Mandatory Batch Selection · the To-Issue qty
                    // now comes exclusively from `batchSum(rm)`. Post Issue
                    // is blocked unless at least one line has ≥ 1 batch qty
                    // > 0, and every allocated line must fit the source
                    // godown's Passed pool (belt + suspenders — backend
                    // also enforces).
                    const shortLines = (picked.lines || []).map(ln => {
                      const val = batchSum(ln.rm_code);
                      if (val <= 0) return null;
                      const cur = stockFor(ln.rm_code, picked.source_godown_code);
                      return val > cur + 1e-4 ? { rm: ln.rm_code, need: val, cur, uom: ln.uom } : null;
                    }).filter(Boolean);
                    const anyIssue = (picked.lines || []).some(ln => batchSum(ln.rm_code) > 0);
                    const godownName = picked.source_godown_code || "source godown";
                    const destBlocked = !!godownInfo?.no_destination || (!!godownInfo?.requires_selection && !destGodownId);
                    const disabled = shortLines.length > 0 || !anyIssue || destBlocked;
                    const title = shortLines.length
                      ? `Insufficient stock in ${godownName}`
                      : (!anyIssue
                          ? "Batch selection is mandatory. Please select specific batches to proceed."
                          : (destBlocked ? "Select a destination godown before posting the issue" : ""));
                    return (
                      <div className="flex flex-col items-end gap-1">
                        <button
                          data-testid="mi-issue-btn"
                          onClick={issue}
                          disabled={disabled}
                          title={title}
                          className={`px-4 py-2 rounded-sm text-white text-sm font-semibold flex items-center gap-2 ${disabled ? "bg-zinc-700 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"}`}
                        >
                          <CheckCircleIcon size={14} weight="bold" />Post Issue
                        </button>
                        {!anyIssue && (
                          <div data-testid="mi-batch-required" className="text-[10px] text-amber-300 font-mono text-right max-w-xs">
                            Batch selection is mandatory. Open the Batches drawer on each line and allocate qty to specific batches.
                          </div>
                        )}
                        {shortLines.length > 0 && (
                          <div data-testid="mi-stock-error" className="text-[10px] text-red-300 font-mono text-right max-w-xs">
                            Insufficient stock in {godownName}. Current balance is
                            {" "}{shortLines.map(s => `${s.cur} ${s.uom || ""}`).join(" · ")}. Cannot proceed with issue.
                          </div>
                        )}
                      </div>
                    );
                  })()
                : <span data-testid="mi-issued-chip" className="px-4 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-xs font-mono uppercase tracking-widest flex items-center gap-2">
                    <CheckCircleIcon size={14} weight="bold" />Fully Issued {(picked.issued_at || "").slice(0,10)}{picked.issued_by ? ` · ${picked.issued_by}` : ""}
                  </span>}
            </div>
          </div>
          {/* P17-UAT MI consolidation · Source (fixed) + Destination Godown
              control. Destination is mandatory and fails closed before Post
              Issue; resolved LIVE from Godown Master by MRQ department. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="mi-godown-control">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Source Godown (fixed)</label>
              <div data-testid="mi-source-godown"
                   className="h-9 px-3 flex items-center rounded-sm border border-[var(--border)] bg-[var(--bg-0)]/60 font-mono text-sm text-amber-300">
                {(godownInfo?.source?.code || picked.source_godown_code || "RM-STORE")}{godownInfo?.source?.name ? ` — ${godownInfo.source.name}` : " — Raw Material Store"}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">
                Destination Godown *{godownInfo?.requires_selection ? " · select receiving floor" : ""}
              </label>
              {godownInfo?.no_destination ? (
                <div data-testid="mi-dest-none"
                     className="h-9 px-3 flex items-center rounded-sm border border-red-500/50 bg-red-500/10 font-mono text-[11px] text-red-300">
                  No godown configured for “{godownInfo.department}” in Godown Master
                </div>
              ) : godownInfo?.requires_selection ? (
                <select data-testid="mi-dest-godown-select" value={destGodownId}
                        onChange={e => setDestGodownId(e.target.value)}
                        className="h-9 px-3 w-full bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]">
                  <option value="">— Select destination floor —</option>
                  {(godownInfo?.destinations || []).map(g => (
                    <option key={g.id} value={g.id}>{g.code} — {g.name}</option>
                  ))}
                </select>
              ) : (
                <div data-testid="mi-dest-godown"
                     className="h-9 px-3 flex items-center rounded-sm border border-[var(--border)] bg-[var(--bg-0)]/60 font-mono text-sm text-emerald-300">
                  {(() => {
                    const g = (godownInfo?.destinations || []).find(x => x.id === destGodownId) || (godownInfo?.destinations || [])[0];
                    return g ? `${g.code} — ${g.name}` : (godownInfo ? "—" : (picked.dest_godown_code || "resolving…"));
                  })()}
                  {godownInfo && <span className="ml-2 text-[9px] uppercase tracking-widest text-zinc-500">auto-selected</span>}
                </div>
              )}
            </div>
          </div>
          <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2 w-8">#</th>
                  <th className="text-left px-3 py-2">Material</th>
                  <th className="text-right px-3 py-2 w-24">Required</th>
                  <th className="text-right px-3 py-2 w-24">Issued-to-Date</th>
                  <th className="text-right px-3 py-2 w-24">Balance</th>
                  <th className="text-right px-3 py-2 w-28" title="Passed (QC-cleared) stock only. Awaiting-Inspection or Rejected lots never appear here.">In Stock · QC ✓</th>
                  <th className="text-right px-3 py-2 w-28">To Issue Now</th>
                  <th className="text-left px-3 py-2 w-14">UoM</th>
                </tr>
              </thead>
              <tbody>
                {(picked.lines || []).map((ln, i) => {
                  const req = lineRequired(ln);
                  const iss = lineIssued(ln);
                  const bal = lineBalance(ln);
                  const cur = stockFor(ln.rm_code, picked.source_godown_code);
                  const val = batchSum(ln.rm_code);
                  const over = val > bal + 1e-4;
                  const short = val > cur + 1e-4;
                  const needsBatch = val <= 0;
                  return (
                    <>
                    <tr key={i} className="border-t border-[var(--border)]/60" data-testid={`mi-line-${ln.rm_code}`}>
                      <td className="px-3 py-2 font-mono text-zinc-500">{i + 1}</td>
                      <td className="px-3 py-2 font-mono text-[var(--accent)]">{ln.rm_code}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-white">{fmt(req)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">{fmt(iss)}</td>
                      <td className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${bal > 0 ? "text-amber-300" : "text-emerald-400"}`}>{fmt(bal)}</td>
                      <td className={`px-3 py-2 text-right font-mono tabular-nums ${cur < val ? "text-red-400" : "text-zinc-300"}`}>{fmt(cur)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <input type="number" step="0.001" min="0"
                            data-testid={`mi-qty-${ln.rm_code}`}
                            value={val > 0 ? val.toFixed(3).replace(/\.?0+$/, "") : ""}
                            readOnly
                            tabIndex={-1}
                            title="Read-only. Click the Batches icon to allocate qty to specific lots."
                            className={`w-24 h-8 px-2 text-right font-mono bg-[var(--bg-0)]/60 border rounded-sm cursor-not-allowed text-zinc-300 ${over || short ? "border-red-500" : (needsBatch ? "border-amber-500/40" : "border-[var(--border)]")}`}
                            placeholder="Pick batch →" />
                          <button
                            data-testid={`mi-batch-toggle-${ln.rm_code}`}
                            title={needsBatch ? "Batch selection required — click to allocate" : "Show / edit batch allocations"}
                            onClick={async () => {
                              const rm = ln.rm_code, next = !batchOpen[rm];
                              setBatchOpen(m => ({ ...m, [rm]: next }));
                              if (next) {
                                let lots = batchesByRm[rm];
                                if (!lots) {
                                  try {
                                    const src = picked.source_godown_code || "RM-STORE";
                                    const { data } = await api.get(`/inventory/available-lots`, {
                                      params: { rm_code: rm, godown_code: src },
                                    });
                                    // Feb 2026 · FIFO · sort oldest → newest so the
                                    // rendered table visually confirms the suggestion.
                                    lots = (data || []).slice().sort((a, b) => {
                                      const da = a.received_date || "";
                                      const db = b.received_date || "";
                                      if (da !== db) return da < db ? -1 : 1;
                                      return (a.batch_no || "").localeCompare(b.batch_no || "");
                                    });
                                    setBatchesByRm(m => ({ ...m, [rm]: lots }));
                                  } catch (e) {
                                    toast.error("Failed to load batches");
                                    return;
                                  }
                                }
                                // Feb 27, 2026 · Auto-prefill removed. The
                                // drawer now opens with empty allocations so
                                // the operator must consciously key each
                                // batch qty and can't accidentally post a
                                // wrong number. Auto FIFO is still available
                                // as an EXPLICIT button click for the common
                                // full-line issue case.
                              }
                            }}
                            className={`h-8 w-8 flex items-center justify-center rounded-sm ${needsBatch ? "text-amber-300 hover:text-white bg-amber-500/10" : "text-zinc-400 hover:text-white hover:bg-[var(--bg-2)]"}`}
                          ><StackIcon size={14} weight="bold" /></button>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-zinc-400 font-mono text-[11px]">{ln.uom}</td>
                    </tr>
                    {batchOpen[ln.rm_code] && (
                      <tr data-testid={`mi-batches-${ln.rm_code}`}>
                        <td colSpan={8} className="px-3 pb-3 bg-[var(--bg-0)]/40">
                          {(() => {
                            const lots = batchesByRm[ln.rm_code] || [];
                            // Feb 25 · Mandatory Batch Selection · target is
                            // driven by lineBalance (default cap); the drawer
                            // header shows the running allocation total.
                            const target = lineBalance(ln);
                            const pickedTotal = lots.reduce((s, l) => s + num(batchQty[`${ln.rm_code}|${l.lot_id}`]), 0);
                            const short = Math.abs(pickedTotal - target) > 0.0001;
                            return (
                              <div className="border border-[var(--border)] rounded-sm p-3 space-y-2">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                                    Active Batches · {ln.rm_code}
                                    <span className="text-[9px] text-emerald-300/80 normal-case tracking-normal">FIFO · oldest first</span>
                                    <span className="text-[9px] text-amber-300/80 normal-case tracking-normal">Batch selection is mandatory</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      data-testid={`mi-fifo-${ln.rm_code}`}
                                      disabled={target <= 0 || lots.length === 0}
                                      onClick={() => {
                                        const res = applyFifo(ln.rm_code, target, lots);
                                        if (res.shortfall > 0) toast.warning(`FIFO short by ${fmt(res.shortfall)} ${ln.uom || ""} — not enough batch balance`);
                                      }}
                                      title="Auto-fill from oldest batches (FIFO)"
                                      className={`h-7 px-2 rounded-sm border text-[10px] font-mono uppercase tracking-widest flex items-center gap-1 ${target > 0 && lots.length > 0 ? "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10" : "border-[var(--border)] text-zinc-600 cursor-not-allowed"}`}
                                    >
                                      <LightningIcon size={11} weight="bold" />Auto FIFO
                                    </button>
                                    <div className={`text-[10px] font-mono ${short && target > 0 ? "text-red-300" : "text-emerald-300"}`}>
                                      {target > 0 ? `Allocated ${fmt(pickedTotal)} / ${fmt(target)} ${ln.uom || ""} (line balance)` : "Line already fully issued"}
                                    </div>
                                  </div>
                                </div>
                                {lots.length === 0
                                  ? <div className="text-[11px] text-zinc-500">No active batches with balance &gt; 0.</div>
                                  : <table className="w-full text-[11px]">
                                      <thead className="text-[9px] uppercase tracking-widest text-zinc-500">
                                        <tr>
                                          <th className="text-left px-2 py-1">Batch #</th>
                                          <th className="text-left px-2 py-1">Received</th>
                                          <th className="text-right px-2 py-1">Available</th>
                                          <th className="text-right px-2 py-1 w-28">Issue Qty</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {lots.map(l => {
                                          const key = `${ln.rm_code}|${l.lot_id}`;
                                          const v = num(batchQty[key]);
                                          const over = v - l.available_qty > 1e-4;
                                          const isFifo = !!fifoFilled[key] && v > 0;
                                          return (
                                            <tr key={l.lot_id} className="border-t border-[var(--border)]/40">
                                              <td className="px-2 py-1 font-mono text-[var(--accent)]">{l.batch_no}</td>
                                              <td className="px-2 py-1 text-zinc-400">{l.received_date}</td>
                                              <td className="px-2 py-1 text-right font-mono text-zinc-300">{fmt(l.available_qty)}</td>
                                              <td className="px-2 py-1 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                  {isFifo && (
                                                    <span
                                                      data-testid={`mi-fifo-badge-${ln.rm_code}-${l.lot_id}`}
                                                      title="Suggested by FIFO (oldest batch first)"
                                                      className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                                                    >FIFO</span>
                                                  )}
                                                  <input type="number" step="0.001" min="0" max={l.available_qty}
                                                    data-testid={`mi-batch-qty-${ln.rm_code}-${l.lot_id}`}
                                                    value={batchQty[key] || ""}
                                                    onChange={e => {
                                                      const val = e.target.value;
                                                      setBatchQty(m => ({ ...m, [key]: val }));
                                                      // Manual override clears the FIFO badge.
                                                      setFifoFilled(m => { const n = { ...m }; delete n[key]; return n; });
                                                    }}
                                                    className={`w-24 h-7 px-2 text-right font-mono bg-[var(--bg-0)] border rounded-sm ${over ? "border-red-500" : "border-[var(--border)]"}`}
                                                    placeholder="0.000" />
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
          {canIssue && (
            <div className="text-[11px] text-zinc-500 font-mono">
              Tip: type the exact qty to issue against each batch. Auto FIFO is available as an explicit one-click helper, but nothing is pre-filled on open — this prevents accidental over-issue.
            </div>
          )}
        </div>
      )}

      {/* Feb 27, 2026 · Reversal Impact Preview dialog. Shows the exact
          cascade blast-radius (linked RMCs to cancel, ledger legs to
          mirror) before the operator commits to reversing the issue. */}
      {revTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !revBusy && setRevTarget(null)}>
          <div className="bg-[var(--bg-1)] border border-[var(--border)] rounded-sm max-w-2xl w-full p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="mi-reverse-dialog">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-amber-300 flex items-center gap-2">
                <ArrowCounterClockwiseIcon size={12} weight="bold" /> Reversal Impact Preview
              </div>
              <div className="text-lg font-mono text-white mt-1">{revTarget.preview.mrq_no}</div>
              <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
                Total issued against this request to date: <span className="text-emerald-300">{Number(revTarget.preview.total_issued || 0).toLocaleString("en-IN")}</span>
              </div>
            </div>

            {revTarget.preview.can_reverse ? (
              <>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Ledger Legs to Mirror", value: revTarget.preview.reversal_legs, color: "text-sky-300" },
                    { label: "Kardex Rows to Mirror", value: revTarget.preview.reversal_txns, color: "text-purple-300" },
                    { label: "Cascade Txn Reversals", value: revTarget.preview.cascade_txn_reversals, color: "text-amber-300" },
                  ].map((k, i) => (
                    <div key={i} className="border border-[var(--border)] rounded-sm p-3" data-testid={`mi-rev-kpi-${i}`}>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k.label}</div>
                      <div className={`text-2xl font-mono mt-1 ${k.color}`}>{k.value}</div>
                    </div>
                  ))}
                </div>

                {(revTarget.preview.would_cancel_rmcs || []).length > 0 && (
                  <div className="border border-amber-500/30 rounded-sm p-3 bg-amber-500/5" data-testid="mi-rev-cascade">
                    <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-1.5">
                      <ProhibitIcon size={11} weight="bold" />
                      {revTarget.preview.would_cancel_rmcs.length} linked RMC{revTarget.preview.would_cancel_rmcs.length === 1 ? "" : "s"} will be cancelled
                    </div>
                    <div className="space-y-1">
                      {revTarget.preview.would_cancel_rmcs.map(rmc => (
                        <div key={rmc.id} data-testid={`mi-rev-rmc-${rmc.challan_no}`} className="text-[11px] font-mono flex items-center justify-between text-zinc-300">
                          <span><span className="text-[var(--accent)]">{rmc.challan_no}</span> · {rmc.status}</span>
                          <span className="text-zinc-500">excess {rmc.excess_qty} · legs {rmc.transfer_legs}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(revTarget.preview.linked_rmcs || []).length === 0 && (
                  <div className="text-[11px] font-mono text-zinc-500 border border-dashed border-[var(--border)] rounded-sm p-3">
                    No linked Returnable Material Challans — plain reversal.
                  </div>
                )}

                <div className="text-[10px] font-mono text-zinc-500 border-t border-[var(--border)] pt-2 leading-relaxed"
                      data-testid="mi-rev-copy">
                  Emergency physical issue correction only — planning / reservations are unchanged. Reservation, MRP and PR records will NOT be restored, replanned or adjusted by this reversal.
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Reversal reason <span className="text-red-400">*</span> (min 4 chars)</label>
                  <textarea data-testid="mi-rev-reason" rows={2} value={revReason} onChange={e => setRevReason(e.target.value)}
                    placeholder="e.g. Wrong material issued · Shop-floor over-consumption undo"
                    className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <button onClick={() => setRevTarget(null)} disabled={revBusy} className="h-9 px-3 border border-[var(--border)] rounded-sm text-xs uppercase tracking-widest hover:bg-[var(--bg-2)]">Cancel</button>
                  <button data-testid="mi-rev-confirm" disabled={revBusy || revReason.trim().length < 4}
                    onClick={async () => {
                      setRevBusy(true);
                      try {
                        const { data } = await api.post(`/material-requisitions/${revTarget.row.id}/reverse-issue`, { reason: revReason.trim() });
                        toast.success(`${data.mrq_no} reversed · ${data.reversal_legs} ledger legs · ${data.reversal_txns} Kardex rows · ${data.linked_rmcs_cancelled || 0} RMC${(data.linked_rmcs_cancelled || 0) === 1 ? "" : "s"} cancelled · ${data.cascade_txn_reversals || 0} cascade legs`);
                        if (picked?.id === revTarget.row.id) setPicked(null);
                        setRevTarget(null);
                        load();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Reversal failed"); }
                      finally { setRevBusy(false); }
                    }}
                    className="h-9 px-4 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-black font-mono text-xs uppercase tracking-widest rounded-sm">
                    {revBusy ? "Reversing…" : "Confirm Reversal"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="border border-red-500/40 bg-red-500/10 rounded-sm p-4 text-sm text-red-200" data-testid="mi-rev-block">
                  {revTarget.preview.block_reason || "This Material Issue Request cannot be reversed."}
                </div>
                {(revTarget.preview.blocking_rmcs || []).length > 0 && (
                  <div className="border border-[var(--border)] rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Blocking RMCs</div>
                    <div className="space-y-1">
                      {revTarget.preview.blocking_rmcs.map(rmc => (
                        <div key={rmc.id} className="text-[11px] font-mono flex items-center justify-between text-zinc-300">
                          <span><span className="text-[var(--accent)]">{rmc.challan_no}</span> · {rmc.status}</span>
                          <span className="text-zinc-500">returned {rmc.returned_qty} · consumed {rmc.consumed_qty}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end">
                  <button data-testid="mi-rev-close" onClick={() => setRevTarget(null)} className="h-9 px-4 border border-[var(--border)] rounded-sm text-xs uppercase tracking-widest hover:bg-[var(--bg-2)]">Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
